import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useTranslation } from '@/lib/i18n';
import {
  Button, PageShell, Skeleton, DataTable, StatusBadge, RowActions, Pagination, Input, SearchGrid, SearchGridItem,
  type DataTableColumn,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose,
} from '@/components/ui';
import { Plus } from 'lucide-react';
import { useAllPopupsQuery, usePopupMutation } from '../hooks/usePopupsQuery';
import { eventPeriodStatus, todayKst, type EventPeriodStatus } from '../lib/eventListFilter';
import { EMPTY_POPUP_FILTER, filterPopups, type PopupListFilter } from '../lib/popupListFilter';

const PAGE_SIZE = 20;

type PopupRow = {
  popupId: string;
  name: string;
  location: string;
  popupKey: string;
  openDate: string;
  closeDate: string;
  sortOrder: number;
  isActive: boolean;
  reservedCount?: number | null;
  capacity?: number | null;
};

// 진행 상태는 오늘(KST) 기준 — 이벤트 관리와 같은 배지(종전엔 UTC 날짜라 자정~09시에 하루 어긋났다).
const STATUS_BADGE: Record<EventPeriodStatus, { variant: 'info' | 'success' | 'warning'; label: string }> = {
  UNSET: { variant: 'info', label: '미정' },
  SCHEDULED: { variant: 'info', label: '예정' },
  ONGOING: { variant: 'success', label: '진행중' },
  ENDED: { variant: 'warning', label: '종료' },
};

/** 라디오 묶음(검색 조건) — 이벤트 관리 검색과 같은 모양. */
const RadioRow = <V extends string>({
  name,
  value,
  options,
  onChange,
}: {
  name: string;
  value: V;
  options: ReadonlyArray<{ value: V; label: string }>;
  onChange: (v: V) => void;
}) => (
  <div className="flex flex-wrap items-center gap-4 text-[13px]" role="radiogroup" data-testid={`popup-filter-${name}`}>
    {options.map((o) => (
      <label key={o.value} className="inline-flex items-center gap-1.5 whitespace-nowrap">
        <input type="radio" name={`popup-filter-${name}`} checked={value === o.value} onChange={() => onChange(o.value)} />
        {o.label}
      </label>
    ))}
  </div>
);

/*
 * 팝업 관리(오프라인 팝업스토어) 목록. QA-356(이벤트 관리와 같은 요청):
 * - 검색 조건: 팝업명·도시(부분일치) · 기간(겹치는 팝업) · 진행 상태 · 활성 여부. BE 가 검색을 받지 않아 전체를 받아 거른다.
 * - 팝업명 칸을 넓히고(종전 280px) 팝업명을 누르면 수정 화면(⋯ 메뉴의 수정 제거).
 */
export const PopupListPage = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [draft, setDraft] = useState<PopupListFilter>(EMPTY_POPUP_FILTER);
  const [filter, setFilter] = useState<PopupListFilter>(EMPTY_POPUP_FILTER);
  const query = useAllPopupsQuery();
  const { remove } = usePopupMutation();
  const [deleteTarget, setDeleteTarget] = useState<PopupRow | null>(null);

  const today = todayKst();
  const filtered = useMemo(
    () => filterPopups((query.data ?? []) as PopupRow[], filter, today),
    [query.data, filter, today],
  );
  const items = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const totalElements = filtered.length;
  const patch = (p: Partial<PopupListFilter>) => setDraft((prev) => ({ ...prev, ...p }));
  const apply = (next: PopupListFilter) => {
    setFilter(next);
    setPage(1);
  };

  const columns: DataTableColumn<PopupRow>[] = [
    {
      key: 'name',
      header: t('cms.popups.cols.name'),
      headerAlign: 'center',
      cell: (row) => (
        <Link
          to={`/cms/popups/${row.popupId}`}
          className="block min-w-[280px] truncate text-sm font-medium hover:underline"
          style={{ color: 'var(--content-strong)' }}
          title={row.name}
          data-testid="popup-name-link"
        >
          {row.name}
        </Link>
      ),
    },
    {
      key: 'location',
      header: t('cms.popups.cols.city'),
      cell: (row) => (
        <span className="block max-w-[160px] truncate text-sm" style={{ color: 'var(--content-default)' }} title={row.location}>
          {row.location}
        </span>
      ),
    },
    {
      key: 'period',
      header: t('cms.popups.cols.period'),
      align: 'center',
      width: 190,
      cell: (row) => (
        <span className="whitespace-nowrap text-xs tabular-nums" style={{ color: 'var(--content-muted)' }}>
          {row.openDate} ~ {row.closeDate}
        </span>
      ),
    },
    {
      key: 'periodStatus',
      header: t('cms.popups.cols.periodStatus', { defaultValue: '진행상태' }),
      align: 'center',
      width: 96,
      cell: (row) => {
        const s = STATUS_BADGE[eventPeriodStatus(row.openDate, row.closeDate, today)];
        return <span className="inline-block whitespace-nowrap"><StatusBadge variant={s.variant} label={s.label} /></span>;
      },
    },
    {
      key: 'reservation',
      header: t('cms.popups.cols.reservation', { defaultValue: '예약/정원' }),
      align: 'right',
      width: 110,
      cell: (row) => (
        <span className="text-sm tabular-nums" style={{ color: 'var(--content-muted)' }}>
          {row.reservedCount ?? 0} / {row.capacity ?? 0}
        </span>
      ),
    },
    {
      key: 'sortOrder',
      header: t('cms.popups.cols.sortOrder'),
      align: 'right',
      width: 88,
      cell: (row) => (
        <span className="text-sm tabular-nums" style={{ color: 'var(--content-muted)' }}>
          {row.sortOrder}
        </span>
      ),
    },
    {
      key: 'isActive',
      header: t('cms.popups.cols.isActive'),
      align: 'center',
      width: 96,
      cell: (row) => (
        <StatusBadge
          variant={row.isActive ? 'success' : 'info'}
          label={row.isActive ? t('cms.active') : t('cms.inactive')}
        />
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: 56,
      cell: (row) => (
        <RowActions
          triggerLabel={t('cms.popups.rowActionsLabel', { defaultValue: '팝업 액션' })}
          actions={[
            {
              key: 'reservations',
              label: t('cms.popups.reservations'),
              onClick: () => navigate(`/cms/popups/${row.popupId}/reservations`),
            },
            {
              key: 'delete',
              label: t('cms.delete'),
              variant: 'danger',
              separatorBefore: true,
              onClick: () => setDeleteTarget(row),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <PageShell
      title={t('cms.popups.title')}
      actions={
        <Link to="/cms/popups/new">
          <Button size="sm">
            <Plus size={15} aria-hidden="true" />
            {t('cms.popups.create')}
          </Button>
        </Link>
      }
      data-testid="popup-list-page"
    >
      <form
        className="mb-4"
        onSubmit={(e) => {
          e.preventDefault();
          apply(draft);
        }}
      >
        <SearchGrid
          aria-label={t('cms.popups.search.label', { defaultValue: '팝업 검색' })}
          testId="popup-search"
          footer={
            <>
              <Button type="submit" size="sm" data-testid="popup-search-submit">
                {t('cms.events.search.submit', { defaultValue: '검색' })}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setDraft(EMPTY_POPUP_FILTER);
                  apply(EMPTY_POPUP_FILTER);
                }}
              >
                {t('cms.events.search.reset', { defaultValue: '초기화' })}
              </Button>
            </>
          }
        >
          <SearchGridItem label={t('cms.popups.search.keyword', { defaultValue: '팝업명·도시' })} htmlFor="popup-keyword">
            <Input
              id="popup-keyword"
              type="search"
              value={draft.keyword}
              onChange={(e) => patch({ keyword: e.target.value })}
              placeholder={t('cms.popups.search.keywordPlaceholder', { defaultValue: '팝업명 또는 도시 일부로 검색' })}
              className="w-full max-w-[360px]"
            />
          </SearchGridItem>
          <SearchGridItem label={t('cms.events.search.period', { defaultValue: '기간' })}>
            <div className="flex items-center gap-1.5">
              <Input type="date" value={draft.from} onChange={(e) => patch({ from: e.target.value })} aria-label={t('cms.events.search.from', { defaultValue: '시작일' })} className="w-full max-w-[170px]" />
              <span className="text-[12px]" style={{ color: 'var(--content-muted)' }}>~</span>
              <Input type="date" value={draft.to} onChange={(e) => patch({ to: e.target.value })} aria-label={t('cms.events.search.to', { defaultValue: '종료일' })} className="w-full max-w-[170px]" />
            </div>
          </SearchGridItem>
          <SearchGridItem label={t('cms.events.search.status', { defaultValue: '진행 상태' })}>
            <RadioRow
              name="status"
              value={draft.status}
              onChange={(status) => patch({ status })}
              options={[
                { value: 'ALL', label: '전체' },
                { value: 'SCHEDULED', label: '예정' },
                { value: 'ONGOING', label: '진행중' },
                { value: 'ENDED', label: '종료' },
              ]}
            />
          </SearchGridItem>
          <SearchGridItem label={t('cms.events.search.active', { defaultValue: '활성 여부' })}>
            <RadioRow
              name="active"
              value={draft.active}
              onChange={(active) => patch({ active })}
              options={[
                { value: 'ALL', label: '전체' },
                { value: 'ACTIVE', label: t('cms.active') },
                { value: 'INACTIVE', label: t('cms.inactive') },
              ]}
            />
          </SearchGridItem>
        </SearchGrid>
      </form>

      {query.isPending ? (
        <div role="status" aria-live="polite" className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-[44px] w-full" />
          ))}
        </div>
      ) : query.isError ? (
        <div
          role="alert"
          aria-live="polite"
          className="flex flex-col items-center gap-2 rounded-lg py-12 text-center text-sm"
          style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
        >
          <p>{t('cms.popups.loadFailed')}</p>
          <Button variant="outline" size="sm" onClick={() => query.refetch()}>
            {t('cms.retry')}
          </Button>
        </div>
      ) : (
        <DataTable
          columns={columns}
          rows={items}
          rowKey={(row) => row.popupId}
          emptyState={t('cms.popups.empty')}
          stickyHeader
        />
      )}

      <Pagination
        total={totalElements}
        currentPage={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        className="mt-4"
      />

      <Dialog open={deleteTarget !== null} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('cms.popups.title')} 삭제</DialogTitle>
            <DialogDescription>
              {t('cms.popups.confirmDelete', { name: deleteTarget?.name ?? '' })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" size="sm">{t('cms.cancel')}</Button>
            </DialogClose>
            <Button
              variant="danger"
              size="sm"
              disabled={remove.isPending}
              onClick={() => {
                if (deleteTarget) remove.mutate(deleteTarget.popupId, { onSuccess: () => setDeleteTarget(null) });
              }}
            >
              {t('cms.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
};
